import USER_ACCOUNT_TABS from '../../models/userAccountTabs.js';
import USER from '../../models/user.js';
import _ from 'lodash';

let MIN_LENGTH_OF_PASSWORD = 8;
let MIN_CHAR_COMBINATIONS_FOR_PASSWORD = 3;

class UserAccountController {
  constructor($stateParams, $state, userAccountService, formValidationService) {
    this.$stateParams = $stateParams;
    this.$state = $state;
    this.userAccountService = userAccountService;
    this.tabs = USER_ACCOUNT_TABS;
    this.user = this.userAccountService.user;
    this.userForm = USER;
    this.activateTab(this.$stateParams.tabName);
    this.isActiveTabEqualsProfile = true;
    this.setUserDetails();
    this.EMAIL_REGEX = formValidationService.EMAIL_REGEX;
    this.setErrorSuccessMsgDefaults();
  }

  setUserDetails() {
    let user = this.userAccountService.getUserDetailsFromClientStorage();

    this.userForm.firstName = user.firstName;
    this.userForm.lastName = user.lastName;
    this.userForm.email = user.email;
    this.userForm.userId = user.userId;
    this.userForm.title = user.title;
    this.userForm.phone = user.phone;
    this.userForm.currentPassword = '';
    this.userForm.newPassword = '';
    this.userForm.confirmNewPassword = '';
  }

  activateTab(tabName) {
    this.clearActiveTabs();

    let activeTabIndex = _.findIndex(this.tabs, (tab) => {
      return tab.name === tabName;
    });

    this.activeTab = this.tabs[activeTabIndex];
    this.activeTab.active = true;
  }

  clearActiveTabs() {
    this.setErrorSuccessMsgDefaults();
    this.resetNewPasswordValidations();

    this.setUserDetails();

    this.tabs.forEach((tab) => {
      tab.active = false;
    });
  }

  setActiveTabFromClick(tabName) {
    this.setIsActiveTabEqualsProfileFlag(tabName);
    this.activateTab(tabName);
    this.$state.go(this.$state.current, {
      tabName: tabName
    }, {
      notify: false,
      reload: this.$state.current
    });
  }

  setIsActiveTabEqualsProfileFlag(tabName) {
    if (tabName === 'password') {
      this.isActiveTabEqualsProfile = false;
    } else
      this.isActiveTabEqualsProfile = true;
  }

  update(formData, formType) {
    let user = {};

    if (formData.$valid) {
      this.hasPendingRequest = true;
      this.setErrorSuccessMsgDefaults();
      user = this.formatRequestBasedOnFormType(formType);
      user = this.formatNullsToEmptyStrings(user);

      this.userAccountService.updateUserDetails(user)
        .then(this.onUserDetailsUpdateSuccess.bind(this), this.onUserDetailsUpdateFailure.bind(this));
    } else {
      this.touchFormFields(formData);
    }
  }

  formatRequestBasedOnFormType(formType) {
    let user = {};

    if (formType === 'user') {
      user = this.getFormattedUserRequest();
    } else {
      user = this.getFormattedPasswordRequest();
    }
    return user;
  }

  getFormattedUserRequest() {

    let user = {
      firstName: this.userForm.firstName,
      lastName: this.userForm.lastName,
      email: this.userForm.email,
      userId: this.userForm.userId,
      title: this.userForm.title,
      phone: this.userForm.phone,
      currentPassword: '',
      newPassword: ''
    };
    return user;
  }

  getFormattedPasswordRequest() {

    let user = {
      firstName: this.user.firstName,
      lastName: this.user.lastName,
      email: this.user.email,
      userId: this.user.userId,
      title: this.user.title,
      phone: this.user.phone,
      currentPassword: this.userForm.currentPassword,
      newPassword: this.userForm.newPassword
    };
    return user;
  }

  formatNullsToEmptyStrings(user) {
    _.each(user, (value, key) => {
      if (value === null) {
        user[key] = '';
      }
    });
    return user;
  }

  onUserDetailsUpdateSuccess(response) {
    this.hasPendingRequest = false;
    angular.copy(this.userForm, this.user);
    this.userAccountService.setUserDetailsInClientStorage();

    if (response.data.message === 'Profile updated successfully') {
      this.isUpdateProfileRequestSuccessful = true;
    } else {
      this.isUpdatePasswordRequestSuccessful = true;
    }
  }

  onUserDetailsUpdateFailure(response) {
    this.hasPendingRequest = false;

    if (response.status === 406 && response.data.message === 'Duplicate Email') {
      this.isEmailDuplicate = true;
    } else if (response.status === 406 && response.data.message === 'Current Password Invalid') {
      this.isCurrentPasswordInvalid = true;
    } else if (response.status === 406 && response.data.message === 'New Password Duplicate') {
      this.isNewPasswordDuplicate = true;
    } else {
      this.isUpdatePasswordRequestFailed = true;
      this.isUpdateProfileRequestFailed = true;
    }
  }

  touchFormFields(userForm) {
    let fields = this.getFormFields(userForm);

    _.each(fields, function(field) {
      if (field.$pristine) {
        field.$setTouched(true);
      }
    });
  }

  getFormFields(userForm) {
    let fields = [];

    _.each(userForm, function(value, key) {
      let firstLetter = key.slice(0, 1);
      if (firstLetter !== '$') {
        fields.push(value);
      }
    });
    return fields;
  }

  isRequiredInputFieldTouchedAndEmpty(userForm, field) {
    return userForm[field].$touched && userForm[field].$error.required;
  }

  isFormFieldErroneous(userForm, field) {
    return userForm[field].$error;
  }

  isFieldExceedingMaxlength(userForm, field) {
    return userForm[field].$error.maxlength;
  }

  isEmailFormatInvalid(userForm, emailHasFocus) {
    if (!emailHasFocus) {
      return userForm.email.$error.pattern;
    }
    return false;
  }

  resetNewPasswordValidations() {
    this.isNewPasswordFormatInvalid = false;
    this.isNewPasswordDuplicate = false;
    this.isNewPasswordFieldNotHavingMinLength = false;
    this.isNewPasswordAndConfirmPasswordNotMatching = false;
  }

  validateNewPassword() {
    this.validateNewPasswordFormat();
    this.validateNewPasswordMinLength();
    this.matchNewPasswordAndConfirmPassword();
  }

  validateNewPasswordFormat() {
    let password = this.userForm.newPassword;
    let hasNumbers = +/\d+/.test(password);
    let hasUpperCase = +/[A-Z]+/.test(password);
    let hasLowerCase = +/[a-z]+/.test(password);
    let hasSpecialChars = /[^A-Za-z0-9]/.test(password);

    this.isNewPasswordFormatInvalid = password && (hasNumbers + hasUpperCase + hasLowerCase + hasSpecialChars < MIN_CHAR_COMBINATIONS_FOR_PASSWORD);
  }

  validateNewPasswordMinLength() {
    let password = this.userForm.newPassword;

    if (password) {
      this.isNewPasswordFieldNotHavingMinLength = password.length < MIN_LENGTH_OF_PASSWORD;
    }
  }

  validateConfirmNewPassword() {
    this.matchNewPasswordAndConfirmPassword();
  }

  resetConfirmNewPasswordValidations() {
    this.isNewPasswordAndConfirmPasswordNotMatching = false;
  }

  matchNewPasswordAndConfirmPassword() {
    let newPassword = this.userForm.newPassword;
    let confirmNewPassword = this.userForm.confirmNewPassword;

    if (newPassword && confirmNewPassword) {
      this.isNewPasswordAndConfirmPasswordNotMatching = newPassword !== confirmNewPassword;
    }
  }

  setErrorSuccessMsgDefaults() {
    this.isCurrentPasswordInvalid = false;
    this.isUpdateProfileRequestSuccessful = false;
    this.isUpdateProfileRequestFailed = false;
    this.hasPendingRequest = false;
    this.isUpdatePasswordRequestSuccessful = false;
    this.isUpdatePasswordRequestFailed = false;
    this.isEmailDuplicate = false;
  }

  isUpdatePasswordButtonDisabled(userForm) {
    if (userForm) {
      return this.hasPasswordFormValidationErrors(userForm);
    }
  }

  hasPasswordFormValidationErrors(userForm) {
    if (userForm.$invalid || this.isNewPasswordAndConfirmPasswordNotMatching || this.isNewPasswordFieldNotHavingMinLength || this.isNewPasswordFormatInvalid || this.isCurrentPasswordInvalid) {
      return true;
    }
  }

  isUpdateProfileButtonDisabled(userForm) {
    if (userForm) {
      return this.hasProfileFormValidationErrors(userForm);
    }
  }

  hasProfileFormValidationErrors(userForm) {
    if (userForm.$invalid || this.isEmailDuplicate) {
      return true;
    }
  }
}

UserAccountController.$inject = ['$stateParams', '$state', 'userAccountService', 'formValidationService'];

export
default UserAccountController;


// WEBPACK FOOTER //
// ./app/components/userAccount/userAccount.controller.js